/**
 * 後台 — 手動新增會員(線下客 / 預登記)
 *
 * 場景:客戶手上有線下客 / 既有訂單,想先把人建進系統,接著用人工建單輸訂單。
 * 建一筆 manual_ 會員(不依賴 LINE uid),status=active。之後對方加 LINE 入會,
 * 系統以手機號自動接上其數位帳戶(Phase 2)。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminCreateMember } from "../../api/admin";
import { TIER_LABEL } from "../../components/TierBadge";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";

export default function MemberCreate() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [tier, setTier] = useState("general");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [detail, setDetail] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState<{ uid: string; name: string } | null>(null); // 手機撞到的現有會員

  useEffect(() => {
    if (role !== "admin") navigate("/admin/login", { replace: true });
  }, [role, navigate]);

  async function save() {
    setConflict(null);
    const nameTrim = name.trim();
    const phoneTrim = phone.trim().replace(/-/g, "");
    if (!nameTrim || !phoneTrim) {
      showToast("姓名 + 手機為必填", "error");
      return;
    }
    if (!/^09\d{8}$/.test(phoneTrim)) {
      showToast("手機格式有誤(09 開頭十位數)", "error");
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminCreateMember({
        name: nameTrim,
        phone: phoneTrim,
        email: email.trim(),
        birthday,
        tier,
        address: { city: city.trim(), district: district.trim(), detail: detail.trim() },
        referred_by_code: referralCode.trim(),
      });
      if (r.status === "ok" && r.line_user_id) {
        showToast("已新增會員", "success");
        navigate(`/admin/members/${r.line_user_id}`);
      } else if (r.status === "phone_taken") {
        setConflict({ uid: r.existing_uid || "", name: r.existing_name || "" });
      } else {
        showToast(`新增未成功:${r.hint || r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-6 py-5 flex items-center gap-3">
        <button onClick={() => navigate("/admin/members")} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">新增會員</h1>
      </header>
      <div className="px-6 mt-6 max-w-2xl space-y-4">
        {conflict && (
          <div className="card flex items-center justify-between gap-3 border border-coral">
            <div className="text-sm text-ink">
              此手機號已是現有會員{conflict.name ? `:${conflict.name}` : ""},不需重複建立。
            </div>
            {conflict.uid && (
              <button
                onClick={() => navigate(`/admin/members/${conflict.uid}`)}
                className="btn-secondary text-sm px-4 py-1.5 whitespace-nowrap"
              >
                前往該會員 →
              </button>
            )}
          </div>
        )}
        <div className="card space-y-4">
          <div className="text-xs text-muted bg-cream p-3 rounded-lg leading-relaxed">
            手動建立的會員可立即用於「人工建單」。等對方之後加 LINE 好友入會,系統會以手機號自動接上其數位帳戶(燕窩存摺 / 送禮)。
          </div>
          <div>
            <label className="label">姓名 *</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="王小姐" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">手機 *</label>
              <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0912345678" />
            </div>
            <div>
              <label className="label">會籍等級</label>
              <select className="input" value={tier} onChange={(e) => setTier(e.target.value)}>
                {Object.entries(TIER_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="選填" />
            </div>
            <div>
              <label className="label">生日</label>
              <input className="input" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">寄送地址(選填)</label>
            <div className="grid grid-cols-2 gap-2">
              <input className="input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="縣市" />
              <input className="input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="區域" />
              <input className="input col-span-2" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="詳細地址" />
            </div>
          </div>
          <div>
            <label className="label">推薦碼(選填,業績分潤歸因)</label>
            <input
              className="input uppercase"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="顧問 / 推薦人推薦碼(對應顧問碼管理頁)"
              maxLength={20}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => navigate("/admin/members")} className="btn-secondary flex-1" disabled={submitting}>
            取消
          </button>
          <button onClick={save} className="btn-primary flex-1" disabled={submitting}>
            {submitting ? "建立中…" : "新增會員"}
          </button>
        </div>
      </div>
    </div>
  );
}
